import React from 'react';
import { Box } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';

interface ElliottFaceProps {
  // Rendered width/height in px (the face scales with it).
  size?: number;
  // Scale + glow "breathing" pulse — used for the standalone framework orb.
  pulse?: boolean;
  // Soft static glow when not pulsing (e.g. as a chat message avatar).
  glow?: boolean;
  sx?: SxProps<Theme>;
}

// Elliott's living face: a soft brandy "head" with two dot eyes that blink
// periodically, a curved smile, and a gentle wander so it reads as alive.
// Shared across every place Elliott appears so the character stays consistent.
const ElliottFace: React.FC<ElliottFaceProps> = ({ size = 44, pulse = false, glow = true, sx }) => (
  <Box
    aria-label="Elliott"
    sx={{
      position: 'relative',
      width: size,
      height: size,
      // Soft head shape — rounded, not a hard square nor a full circle.
      borderRadius: '44%',
      flexShrink: 0,
      background: 'radial-gradient(circle at 35% 30%, #F79420 0%, #F15A26 70%)',
      ...(pulse
        ? {
            animation: 'elliottOrbPulse 1.9s ease-in-out infinite',
            // Keep the glow tight so it doesn't spill past a surrounding container.
            '@keyframes elliottOrbPulse': {
              '0%, 100%': { transform: 'scale(1)', boxShadow: '0 0 4px 0 rgba(244,119,35,0.5)' },
              '50%': { transform: 'scale(1.06)', boxShadow: '0 0 10px 1px rgba(247,148,32,0.85)' }
            }
          }
        : glow
        ? { boxShadow: '0 0 12px rgba(244,119,35,0.5)' }
        : {}),
      '&:hover': { filter: 'brightness(1.08)' },
      // Face — gentle wander; eyes blink periodically.
      '& .elliott-face': { animation: 'elliottFaceWander 3.6s ease-in-out infinite' },
      '& .elliott-eye': {
        transformBox: 'fill-box',
        transformOrigin: 'center',
        animation: 'elliottBlink 4.4s ease-in-out infinite'
      },
      '@keyframes elliottFaceWander': {
        '0%, 100%': { transform: 'translate(0, 0)' },
        '25%': { transform: 'translate(-0.6px, 0.5px)' },
        '50%': { transform: 'translate(0, 1px)' },
        '75%': { transform: 'translate(0.6px, 0.5px)' }
      },
      '@keyframes elliottBlink': {
        '0%, 90%, 100%': { transform: 'scaleY(1)' },
        '95%': { transform: 'scaleY(0.12)' }
      },
      ...sx
    }}
  >
    <Box
      component="svg"
      viewBox="0 0 44 44"
      sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
    >
      <g className="elliott-face">
        <circle className="elliott-eye" cx="16.5" cy="17" r="2.3" fill="#FDFDFD" />
        <circle className="elliott-eye" cx="27.5" cy="17" r="2.3" fill="#FDFDFD" />
        {/* Open, smiling mouth — filled white */}
        <path className="elliott-smile" d="M15.5 26.5 Q22 28.5 28.5 26.5 Q22 35.5 15.5 26.5 Z" fill="#FDFDFD" />
      </g>
    </Box>
  </Box>
);

export default ElliottFace;
